import React, { useState } from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  DateTimePicker,
  Modal,
  Select,
  TextField,
  Typography,
} from '@ublend-npm/aulaui-core';
import { LtiAssignmentCreationParameters } from '@ublend-npm/aula-schema';
import { FormUtils } from '@app/hooks/useYupForm/useYupForm';

type DateFieldKey = 'openDate' | 'dueDate' | 'gradesReleaseDate';

const DATE_FIELDS: { key: DateFieldKey; label: string }[] = [
  { key: 'openDate', label: 'Open date' },
  { key: 'dueDate', label: 'Due date' },
  { key: 'gradesReleaseDate', label: 'Grades release date' },
];

type InsperaSetupFormProps = {
  getFieldProps: FormUtils<Record<string, string>>['getFieldProps'];
  extraParameters?: LtiAssignmentCreationParameters;
  showFormInfoMessage?: boolean;
};

const InsperaSetupForm = ({
  getFieldProps,
  extraParameters = [],
  showFormInfoMessage = false,
}: InsperaSetupFormProps) => {
  const [openDateField, setOpenDateField] = useState<DateFieldKey | null>(null);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);

  const textProps = (field: string) => {
    const { set, value, error, label, helperText } = getFieldProps(field);
    return {
      label,
      value: value || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => set(e.target.value),
      errorText: error ? `${label} is required` : undefined,
      helperText: helperText || undefined,
      sx: { m: '0px' },
    };
  };

  const dateDisplayValue = (field: DateFieldKey) => {
    const { value } = getFieldProps(field);
    return value ? moment(value).format('lll') : '';
  };

  const dateError = (field: DateFieldKey) => {
    const { error, label } = getFieldProps(field);
    return error ? `${label} is required` : undefined;
  };

  const handleDateConfirm = (key: DateFieldKey, date: Date) => {
    const normalized = new Date(date);
    normalized.setSeconds(0, 0);
    getFieldProps(key).set(normalized.toISOString());
    setPendingDate(null);
    setOpenDateField(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: { xs: '0.5rem', md: '1rem' },
      }}
    >
      {/* Title */}
      <TextField {...textProps('title')} aria-label="Assignment title" />

      {/* Description */}
      <TextField {...textProps('description')} kind="multiline" maxRows={3} />

      {/* Date fields */}
      {DATE_FIELDS.map(({ key, label }) => (
        <TextField
          key={key}
          label={label}
          value={dateDisplayValue(key)}
          sx={{ cursor: 'pointer', m: '0px' }}
          errorText={dateError(key)}
          inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
          onClick={() => {
            const current = getFieldProps(key).value;
            setPendingDate(current ? new Date(current) : new Date());
            setOpenDateField(key);
          }}
        />
      ))}

      {/* Max grade */}
      <TextField {...textProps('maxGrade')} />

      {/* Extra parameters — dropdowns (e.g. Template) */}
      {extraParameters.map((param, index) => {
        const { set, value, error, label } = getFieldProps(param.key);
        const isLast = index === extraParameters.length - 1;
        return (
          <Select
            key={param.key}
            label={label || param.label}
            value={value || ''}
            options={param.options.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set(e.target.value)
            }
            errorText={error ? `${param.label} is a required field` : undefined}
            helperText={
              isLast && showFormInfoMessage && !error
                ? 'Please note: Assignments must be activated in the tool and set as visible in Aula before students can view and submit'
                : undefined
            }
          />
        );
      })}

      {/* Date picker modal */}
      <Modal open={!!openDateField} onClose={() => setOpenDateField(null)}>
        <Box
          sx={{
            borderRadius: 2,
            boxShadow: 24,
            width: 320,
            maxWidth: '95vw',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ bgcolor: 'background.paper', px: 2, pt: 1.5, pb: 0 }}>
            <Typography
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '12px',
                letterSpacing: '0.08em',
              }}
            >
              Select date &amp; time
            </Typography>
          </Box>
          <DateTimePicker
            key={openDateField}
            value={pendingDate || new Date()}
            onChange={(date: any) => {
              const d = date instanceof Date ? date : date?.toDate?.() ?? null;
              if (d) setPendingDate(d);
            }}
            sx={{
              width: '100%',
              boxShadow: 'none',
              '& .MuiPickersDay-root.Mui-selected:hover': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 2,
              pb: 1.5,
              pt: 0.5,
            }}
          >
            <Button kind="secondary" onClick={() => setOpenDateField(null)}>
              Cancel
            </Button>
            <Button
              kind="secondary"
              onClick={() => {
                if (openDateField) {
                  handleDateConfirm(openDateField, pendingDate ?? new Date());
                }
              }}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default InsperaSetupForm;
